/**
 * LiveEmbed — Public-facing standalone preview endpoint.
 *
 * React Embed mimarisi (preview = site tek source of truth):
 * Panel'de kullanılan ekact aynı preview component'leri (PanelPreview,
 * SizeChartPreviewCanvas, WizardPreview) burada public olarak render edilir.
 * Site'a inject edilen `widget.js` bu route'u iframe olarak mount eder.
 *
 * URL: /live-embed/:surface/:siteKey/:sku
 *   surface: cta | size-chart | find-my-size
 *   siteKey: tenant site_key (sk_demo_sayz_public)
 *   sku: product SKU
 *
 * Public — no auth. Tenant verisi public `/api/widget/config` + `/widget/product`
 * endpoint'lerinden çekilir.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";

import { PanelPreview } from "@/pages/WidgetSizingPanel";
import { SizeChartPreviewCanvas } from "@/pages/SizeChartModal";
import { WizardPreviewProvider, WizardPreviewModal } from "@/components/find-my-size/WizardPreview";
import { SayzSignature } from "@/components/widget-cta/SayzSignature";
import { getSayzSessionId } from "@/lib/sayz-widget-session";

const API = typeof window !== "undefined" ? window.location.origin : "";

// Default values when panel-level data missing
const DEFAULT_FIT_LABELS = {
    tight: "Dar Kalıp",
    normal: "Normal Kalıp",
    relaxed: "Rahat Kalıp",
};

const DEFAULT_FIT_ADVICE = {
    tight: "Normalden bir beden büyüğünü tercih edebilirsiniz.",
    normal: "Kendi bedeninizi alabilirsiniz.",
    relaxed: "Normalden bir beden küçüğünü tercih edebilirsiniz.",
};

// Build the `sample` prop expected by PanelPreview + SizeChart + Wizard previews.
// Matches the shape built in WidgetSizingPanel.jsx (line 239).
// Nullable semantics (per widget visibility rules):
//   fit    — null if merchant hasn't set a fit on this product → kalıp bloğu gizlenir
//   model  — null if no model assigned → model bloğu gizlenir
//   table  — null if no size table bound (or passive) → size_table & find_my_size blokları gizlenir
const buildSample = (product, model, fit, table) => ({
    product: product ? { sku: product.sku, name: product.name } : { sku: "DEMO" },
    fit: (fit && fit.type) ? { type: fit.type } : null,
    model: model || null,
    table: table || null,
});

const applyDimension = (surface) => {
    // Default body margin + widget container has its own padding
    document.documentElement.classList.add("sayz-live-embed");
    document.body.classList.add("sayz-live-embed-body");
    document.body.setAttribute("data-surface", surface);
    // Modal surface'leri için body'ye koyu backdrop — canlı widget'ta
    // ModalPortal overlay'inin yaptığı iş, standalone live-embed URL
    // (admin preview) için body-level ile simule ediliyor. CTA surface'i
    // iframe'de merchant sayfasına embed olduğu için transparan kalır.
    if (surface === "size-chart" || surface === "find-my-size") {
        document.body.style.background = "rgba(5,8,12,0.90)";
        document.body.style.minHeight = "100vh";
        document.body.style.display = "flex";
        document.body.style.alignItems = "center";
        document.body.style.justifyContent = "center";
        document.body.style.margin = "0";
    } else {
        document.body.style.background = "";
        document.body.style.minHeight = "";
        document.body.style.display = "";
        document.body.style.alignItems = "";
        document.body.style.justifyContent = "";
    }
};

export default function LiveEmbed() {
    const { surface, siteKey, sku } = useParams();
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const [state, setState] = useState({ loading: true, error: null, config: null, product: null });

    // Fetch config + product. Priority:
    //   1. Parent pushes via postMessage (prod-grade: one fetch per PDP shared
    //      across all 3 iframes).
    //   2. Direct API fetch (fallback for /live-embed/* loaded outside the
    //      widget bundle, e.g. the panel preview iframe or dev debugging).
    useEffect(() => {
        applyDimension(surface);
        let cancelled = false;
        let gotFromParent = false;

        // Listen for parent push.
        const onMessage = (e) => {
            const data = e.data || {};
            if (!data || !data.sayz || data.type !== "data") return;
            if (cancelled) return;
            if (!data.config || !data.product) return;
            gotFromParent = true;
            setState({ loading: false, error: null, config: data.config, product: data.product });
        };
        window.addEventListener("message", onMessage);

        // Signal readiness to the parent (if we're inside an iframe).
        try {
            if (window.parent && window.parent !== window) {
                window.parent.postMessage({ sayz: true, type: "ready", surface }, "*");
            }
        } catch (_) { /* noop */ }

        // Fallback: fetch directly if parent hasn't pushed within 150ms.
        // (In practice the parent sends `data` almost immediately after
        // receiving our `ready` message, so this hardly ever runs.)
        const timeoutId = setTimeout(async () => {
            if (gotFromParent || cancelled) return;
            try {
                const [cfgRes, prodRes] = await Promise.all([
                    axios.get(`${API}/api/widget/config`, { params: { key: siteKey } }),
                    axios.get(`${API}/api/widget/product`, {
                        params: {
                            key: siteKey,
                            sku,
                            widget: "init",
                            session_id: getSayzSessionId(),
                        },
                    }),
                ]);
                if (cancelled || gotFromParent) return;
                setState({ loading: false, error: null, config: cfgRes.data, product: prodRes.data });
            } catch (err) {
                if (cancelled) return;
                setState({ loading: false, error: err.message || "Fetch failed", config: null, product: null });
            }
        }, 150);

        return () => {
            cancelled = true;
            clearTimeout(timeoutId);
            window.removeEventListener("message", onMessage);
        };
    }, [surface, siteKey, sku]);

    // ---------- iframe <-> parent protocol ----------
    // Height sync: emit document.body.scrollHeight on load + whenever content changes
    useEffect(() => {
        if (window.parent === window) return; // not in iframe

        const postHeight = () => {
            try {
                const h = Math.max(
                    document.documentElement.scrollHeight,
                    document.body?.scrollHeight || 0,
                );
                window.parent.postMessage({ sayz: true, type: "height", surface, value: h }, "*");
            } catch (e) { /* noop */ }
        };
        postHeight();
        const ro = new ResizeObserver(postHeight);
        if (document.body) ro.observe(document.body);
        // Also report after a tick (fonts/images loaded)
        const t1 = setTimeout(postHeight, 400);
        const t2 = setTimeout(postHeight, 1200);
        return () => { ro.disconnect(); clearTimeout(t1); clearTimeout(t2); };
    }, [surface, state.loading]);

    // CTA click interception (CTA surface only): bubble clicks on action buttons
    // to parent so the host site can open the modal iframe.
    useEffect(() => {
        if (window.parent === window) return;
        if (surface !== "cta") return;
        const handler = (e) => {
            const el = e.target.closest('[data-sayz-action], [data-testid^="sp-preview-btn-"], [data-action^="sp-preview-btn-"]');
            if (!el) return;
            e.preventDefault();
            e.stopPropagation();
            // Resolve action key
            let action = el.getAttribute("data-sayz-action");
            if (!action) {
                const t = el.getAttribute("data-action") || el.getAttribute("data-testid") || "";
                if (t.endsWith("find_my_size")) action = "find_my_size";
                else if (t.endsWith("size_table")) action = "size_table";
            }
            if (!action) return;
            try {
                window.parent.postMessage({ sayz: true, type: "cta-click", action, sku }, "*");
                // eslint-disable-next-line no-console
                console.log("[sayz] cta-click posted:", action);
            } catch (err) { /* noop */ }
        };
        document.addEventListener("click", handler, true);
        return () => document.removeEventListener("click", handler, true);
    }, [surface, sku]);

    const { loading, error, config, product } = state;

    if (loading) {
        return (
            <div style={{ padding: 24, fontFamily: "system-ui", color: "#666", fontSize: 13 }}>
                Yükleniyor…
            </div>
        );
    }

    if (error || !config) {
        return (
            <div style={{ padding: 24, fontFamily: "system-ui", color: "#c00", fontSize: 13 }}>
                Widget yüklenemedi. {error ? `(${error})` : ""}
            </div>
        );
    }

    // Route-driven surface switch — Beden Tablosu modalındaki "Bedenimi
    // Bul" CTA'ya (ve Nazik Ret ekranındaki "Beden Tablosu" CTA'ya)
    // tıklandığında kullanılır. `useNavigate` ile SPA içinde reload-less
    // geçiş: React tree remount olur (route değiştiği için `surface` param
    // güncellenir, `LiveEmbedView` yeni surface'i render eder) ama tüm
    // app bundle + Shadow DOM tabanlı widget state'leri korunur, sayfa
    // flash etmez. Iframe-based entegrasyonlar için parent'a hâlâ
    // `postMessage` ile haber verilir ki parent iframe src'ini
    // güncelleyebilsin (dışardaki iframe sunucu-sarıcı için).
    const handleSwitchSurface = (nextSurface) => {
        if (!nextSurface || !siteKey || !sku) return;
        const path = `/live-embed/${nextSurface}/${encodeURIComponent(siteKey)}/${encodeURIComponent(sku)}`;
        if (window.parent && window.parent !== window) {
            try { window.parent.postMessage({ sayz: true, type: "switch-surface", surface: nextSurface, path }, "*"); } catch (_) {}
        }
        // SPA navigation — no page reload, no bundle re-fetch, no state loss.
        navigate(path);
    };

    return <LiveEmbedView surface={surface} siteKey={siteKey} sku={sku} config={config} product={product} onSwitchSurface={handleSwitchSurface} />;
}

/**
 * LiveEmbedView — Pure view component.
 *
 * Takes already-fetched `config` + `product` and renders one of the three
 * widget surfaces (`cta`, `size-chart`, `find-my-size`). Shared by:
 *   • The default `LiveEmbed` export (router-driven, used by the panel's
 *     `/live-embed/*` debug routes).
 *   • The Shadow DOM web component in `frontend-widget/src/WidgetApp.jsx`,
 *     which fetches once at the host level and passes the data in as props.
 *
 * Keeping all prop derivation in a single place prevents the Panel ↔ Live
 * parity from drifting as config shapes evolve.
 */
export function LiveEmbedView({ surface, siteKey, sku, apiBase, config, product, onClose, onSwitchSurface }) {
    // Extract panel-level config bits
    const panelCfg = config?.widgets?.sizing_panel?.config || {};
    const template = panelCfg.template || "minimal";
    const theme = panelCfg.theme || "light";
    const tokens = panelCfg.tokens || {};
    const blocks = Array.isArray(panelCfg.blocks) ? panelCfg.blocks : [];
    const groupOrder = Array.isArray(panelCfg.groupOrder) ? panelCfg.groupOrder : ["info", "action"];
    const fitLabels = useMemo(() => {
        const fi = blocks.find((b) => b.key === "fit_info")?.content || {};
        return {
            labels: {
                tight: fi.tightLabel || DEFAULT_FIT_LABELS.tight,
                normal: fi.normalLabel || DEFAULT_FIT_LABELS.normal,
                relaxed: fi.relaxedLabel || DEFAULT_FIT_LABELS.relaxed,
            },
            advice: {
                tight: fi.tightAdvice || DEFAULT_FIT_ADVICE.tight,
                normal: fi.normalAdvice || DEFAULT_FIT_ADVICE.normal,
                relaxed: fi.relaxedAdvice || DEFAULT_FIT_ADVICE.relaxed,
            },
        };
    }, [blocks]);

    const sample = useMemo(
        () => buildSample(product?.product, product?.model, product?.fit, product?.table),
        [product],
    );

    // ---- CTA Widget (panel preview) ----
    if (surface === "cta") {
        return (
            <div data-live-embed="cta" style={{ padding: 0, background: "transparent" }}>
                <PanelPreview
                    template={template}
                    theme={theme}
                    tokens={tokens}
                    blocks={blocks}
                    sample={sample}
                    groupOrder={groupOrder}
                    fitLabels={fitLabels}
                />
            </div>
        );
    }

    // ---- Size Chart Modal ----
    if (surface === "size-chart") {
        // Guard: no size table bound to this product → modal yüklenmez,
        // CTA zaten gizli olduğundan buraya kullanıcı akışıyla gelmemesi gerek,
        // ama programatik tetikleyiciye karşı savunma katmanı.
        if (!sample.table) return null;

        // Chart settings (block order, cross-CTA text, showTableName, displayType …)
        // are saved by the Beden Tablosu page as `blocks[size_table].content.chart`
        // in the SAME sizing_panel widget settings doc. The backend lifts the
        // `chart` dict up to the top-level `product.chart` field so the live
        // widget can hydrate without a second fetch.
        const stCfg = (product?.chart && typeof product.chart === "object") ? product.chart : {};
        const accent = tokens.accent || "#2B2A27";
        const sizeTableBlock = blocks.find((b) => b.key === "size_table")?.content || {};
        const modalTitle = sizeTableBlock.panelTitle || sizeTableBlock.buttonText || "Ölçülere Bak";
        const findMySizeCta = (blocks.find((b) => b.key === "find_my_size")?.content?.buttonText) || "Bedenimi Bul";

        // Mirror shape expected by SizeChartPreviewCanvas (settings prop).
        // DEFAULTS mirrors /app/frontend/src/pages/SizeChartModal.jsx (modal-only MVP).
        // Blok sırası: table (locked, en üst) → image ↔ how-to-measure (swap) →
        // cross-cta (locked, en alt). Live widget da panel sırasıyla birebir
        // aynı dizilime uyar; eski persisted blockOrder'lar runtime'da normalize edilir.
        const DEFAULT_ORDER = ["table", "image", "how-to-measure", "cross-cta"];
        const incomingMiddle = (Array.isArray(stCfg.blockOrder) ? stCfg.blockOrder : [])
            .filter((k) => k === "image" || k === "how-to-measure");
        const middleFallback = ["image", "how-to-measure"].filter((k) => !incomingMiddle.includes(k));
        const normalizedOrder = ["table", ...incomingMiddle, ...middleFallback, "cross-cta"];
        const settings = {
            displayType: stCfg.displayType || "table",
            showTableName: !!stCfg.showTableName,
            imageEnabled: stCfg.imageEnabled !== false,
            tableEnabled: true,
            howToMeasureEnabled: stCfg.howToMeasureEnabled !== false,
            crossCtaEnabled: stCfg.crossCtaEnabled !== false,
            crossCtaText: stCfg.crossCtaText || "Bedenini bulamadın mı?",
            crossCtaDesc: stCfg.crossCtaDesc || "Kısa bir quiz ile sana en uygun bedeni önerelim.",
            blockOrder: normalizedOrder,
        };

        // SizeChart `sample` shape: { name, sizes:[], rows:[{label, values:[]}] }
        // (different from the CTA sample {product, fit, model}).
        // Convert API table.values map (keyed "Measure|Size") → row.values array.
        // measures may be new-format objects ({name, kind}) or legacy strings;
        // always derive a plain string `label` (never a raw object — React would crash
        // with error #31 "Objects are not valid as a React child").
        const tableData = product?.table;
        const sizeChartSample = tableData ? {
            name: tableData.name || "Beden Tablosu",
            sizes: tableData.sizes || [],
            rows: (tableData.measures || []).map((measure) => {
                const label = (measure && typeof measure === "object")
                    ? String(measure.name || "")
                    : String(measure || "");
                return {
                    label,
                    values: (tableData.sizes || []).map((sz) => {
                        const v = tableData.values?.[`${label}|${sz}`];
                        const n = parseFloat(v);
                        return Number.isFinite(n) ? n : (v || "");
                    }),
                };
            }),
        } : { name: "Beden Tablosu", sizes: ["XS","S","M","L","XL"], rows: [
            { label: "Göğüs", values: [86,90,94,98,102] },
            { label: "Bel",   values: [62,66,70,74,78] },
            { label: "Kalça", values: [90,94,98,102,106] },
        ]};

        return (
            <div
                data-live-embed="size-chart"
                style={{
                    padding: 0,
                    background: "transparent",
                    // Modal shell flex-column; signature'a yer kalsın diye
                    // bu wrapper da flex-column + minHeight:0 olmalı ki
                    // içerideki modal `flex:1` olarak büzülebilsin ve toplam
                    // modal+imza her zaman shell maxHeight (100dvh-32)
                    // içinde kalsın. Masaüstünde shell maxHeight 760px
                    // olduğundan davranış aynı.
                    display: "flex",
                    flexDirection: "column",
                    minHeight: 0,
                    width: "100%",
                    height: "100%",
                }}
            >
                <div
                    style={{
                        width: "100%",
                        maxWidth: 560,
                        margin: "0 auto",
                        display: "flex",
                        flexDirection: "column",
                        minHeight: 0,
                        flex: 1,
                    }}
                >
                    <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
                        <SizeChartPreviewCanvas
                            settings={settings}
                            accent={accent}
                            sample={sizeChartSample}
                            modalTitle={modalTitle}
                            findMySizeCta={findMySizeCta}
                            bodyImage={product?.bodyImage || null}
                            previewGuides={product?.guides || []}
                            widgetTemplate={template}
                            widgetTokens={tokens}
                            onClose={onClose}
                            onSwitchToFindMySize={onSwitchSurface ? () => onSwitchSurface("find-my-size") : undefined}
                        />
                    </div>
                    {/* Modal shell'in DIŞINDA — sağa yaslı Sayz imzası.
                        `flexShrink: 0` sayesinde modal büyüse bile imza
                        kendi yüksekliğini korur; shell maxHeight içinde
                        modal daralır, imza her zaman kesilmeden görünür. */}
                    <div style={{ display: "flex", justifyContent: "flex-end", padding: "12px 0 0 0", flexShrink: 0 }}>
                        <SayzSignature />
                    </div>
                </div>
            </div>
        );
    }

    // ---- Find My Size Wizard ----
    if (surface === "find-my-size") {
        // Guard: Bedenimi Bul tabloyu kullanarak RBF skorlar — tablo yoksa
        // sorulacak bir şey yok. CTA zaten gizli; bu, programatik tetikleyiciye
        // karşı savunma katmanı.
        if (!sample.table) return null;

        // FMS wizard ayarları paneldeki `FindMySizeWizard.jsx` tarafından
        // `sizing_panel.config.blocks[find_my_size].content.wizard` içine
        // kaydedilir. Burada tam olarak o yoldan okuyoruz — eski (ve hiç
        // yazılmamış) `widgets.find_my_size.config` kaynağı artık yok.
        const fmsBlock = blocks.find((b) => b.key === "find_my_size")?.content || {};
        const fmsWizardRaw = (fmsBlock.wizard && typeof fmsBlock.wizard === "object") ? fmsBlock.wizard : {};
        // `siteIntegration` kaydı iki yere yazılmış olabilir:
        //   • Yeni "Bedenimi Bul > Site Entegrasyonu" tab'ı (FindMySizeWizard panel UI)
        //     → `blocks.find_my_size.content.wizard.siteIntegration` (bugünkü source of truth)
        //   • Eski üst-level "Sayz Widget > Site Integration" paneli (legacy, bazı
        //     tenant'larda hâlâ geçerli — ör. demo)
        //     → `sizing_panel.config.siteIntegration` (top-level)
        // Doğru çözüm: ÖNCE wizard path'ini oku (bugünkü merchant akışı), boşsa
        // top-level'a düş (legacy fallback). Böylece hem yeni config'leyen hem
        // de eski config'li tenant tek kod yolu üzerinden çalışır — veri
        // migration'a gerek kalmadan.
        const resolvedSiteIntegration =
            (fmsWizardRaw.siteIntegration && typeof fmsWizardRaw.siteIntegration === "object" &&
             (fmsWizardRaw.siteIntegration.enabled === true || fmsWizardRaw.siteIntegration.sizeSelector))
                ? fmsWizardRaw.siteIntegration
                : panelCfg.siteIntegration;
        const fmsWizard = { ...fmsWizardRaw, siteIntegration: resolvedSiteIntegration };
        const accent = tokens.accent || "#2B2A27";
        // Modal başlığı: merchant Sayz Widget sayfasında CTA metnini
        // (buttonText) değiştirdiğinde modal başlığı da birebir takip etsin.
        // Legacy `modalTitle` alanı eski seed'lerden "Bedenini Bul" olarak
        // kalabilir — bu yüzden buttonText her zaman önce kazanır. SizeChart
        // modal'ındaki `panelTitle || buttonText` davranışıyla parity.
        const modalTitle = fmsBlock.buttonText || fmsBlock.modalTitle || "Bedenimi Bul";
        // Required body fields: migration sonrası measure'lar yeni formatta
        // ({name, kind}) saklanıyor; legacy string[] fallback'i de destekle.
        // Vücut çevresi kind'ları (chest/waist/hip/shoulder) → body:X adımı.
        // Ürün boyu kind'ları (product_length/sleeve/inseam) → "height" sinyali
        // (BasicInfoStep bu sinyale bakıp Boy alanına "Zorunlu" rozeti koyar).
        // "product" ve bilinmeyen → vücut sorusu tetiklemez, tabloda görünür.
        const MEAS_NAME_TO_KIND = {
            "Göğüs": "chest", "göğüs": "chest",
            "Bel": "waist", "bel": "waist",
            "Kalça": "hip", "kalça": "hip",
            "Omuz": "shoulder", "omuz": "shoulder",
            "Chest": "chest", "chest": "chest",
            "Waist": "waist", "waist": "waist",
            "Hip": "hip", "Hips": "hip", "hip": "hip", "hips": "hip",
            "Shoulder": "shoulder", "Shoulders": "shoulder", "shoulder": "shoulder",
            // Ürün boyu alias'ları → "height" (basic step rozeti için)
            "Boy": "height", "boy": "height",
            "Ön Boy": "height", "Arka Boy": "height",
            "Ön Orta Boy": "height", "Arka Orta Boy": "height",
            "Elbise Boyu": "height", "Etek Boyu": "height",
            "Ürün Boyu": "height",
            "Kol Boyu": "height", "Kol Uzunluğu": "height",
            "Sleeve": "height", "Sleeve Length": "height",
            "İç Bacak": "height", "iç bacak": "height",
            "Bacak İçi": "height", "Bacak Boyu": "height",
            "Inseam": "height",
        };
        const PRODUCT_LENGTH_KINDS = new Set(["product_length", "product_sleeve", "product_inseam"]);
        const requiredBodyFields = [];
        (product?.table?.measures || []).forEach((m) => {
            // Yeni format: {name, kind} — kind "product" ise atla; product_length
            // ailesi ise "height"a dönüştür.
            if (m && typeof m === "object") {
                const rawKind = m.kind;
                if (rawKind === "product" || !rawKind) return;
                const normalized = PRODUCT_LENGTH_KINDS.has(rawKind) ? "height" : rawKind;
                if (!requiredBodyFields.includes(normalized)) {
                    requiredBodyFields.push(normalized);
                }
                return;
            }
            // Legacy string fallback: isim → kind (height dahil)
            const k = MEAS_NAME_TO_KIND[String(m)] || null;
            if (k && !requiredBodyFields.includes(k)) requiredBodyFields.push(k);
        });
        const sizeTableButtonText = (blocks.find((b) => b.key === "size_table")?.content?.buttonText) || "Ölçülere Bak";

        return (
            <div
                data-live-embed="find-my-size"
                style={{
                    padding: 0,
                    background: "transparent",
                    // Modal shell flex-column; signature'a yer kalsın diye
                    // bu wrapper da flex-column + minHeight:0 olmalı ki
                    // içerideki wizard modal `flex:1` olarak büzülebilsin
                    // ve toplam modal+imza her zaman shell maxHeight
                    // (100dvh-32) içinde kalsın.
                    display: "flex",
                    flexDirection: "column",
                    minHeight: 0,
                    width: "100%",
                    height: "100%",
                }}
            >
                <div
                    style={{
                        width: "100%",
                        maxWidth: 520,
                        margin: "0 auto",
                        display: "flex",
                        flexDirection: "column",
                        minHeight: 0,
                        flex: 1,
                    }}
                >
                    <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
                        <WizardPreviewProvider
                            accent={accent}
                            settings={fmsWizard}
                            modalTitle={modalTitle}
                            requiredBodyFields={requiredBodyFields.length ? requiredBodyFields : ["chest", "waist", "hip"]}
                            widgetTemplate={template}
                            widgetTokens={tokens}
                            sizeTableButtonText={sizeTableButtonText}
                            onClose={onClose}
                            onOpenSizeChart={onSwitchSurface ? () => onSwitchSurface("size-chart") : undefined}
                            liveSiteKey={siteKey}
                            liveSku={sku}
                            liveApiBase={apiBase}
                        >
                            <WizardPreviewModal />
                        </WizardPreviewProvider>
                    </div>
                    {/* Modal shell'in DIŞINDA — sağa yaslı Sayz imzası.
                        `flexShrink: 0` sayesinde wizard büyüse bile imza
                        kendi yüksekliğini korur; shell maxHeight içinde
                        wizard daralır, imza her zaman kesilmeden görünür. */}
                    <div style={{ display: "flex", justifyContent: "flex-end", padding: "12px 0 0 0", flexShrink: 0 }}>
                        <SayzSignature />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={{ padding: 24, color: "#999", fontSize: 13 }}>
            Unknown surface: {surface}
        </div>
    );
}
